import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Cpu, Search, CheckCircle, Database, Layers, 
  Target, BookOpen, Zap, Filter, Tag, Users, Building2, Landmark,
  MessageSquare, Palette, Code, Kanban
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getCategoryStats, getLabStats, getVerifiedTools, AI_REGISTRY } from "@/data/aiRegistry";
import BackNavigation from "@/components/BackNavigation";

// Icon mapping for categories
const CATEGORY_ICONS = {
  "content-writing": BookOpen,
  "data-analytics": Database,
  "automation-workflow": Zap,
  "communication": MessageSquare,
  "research-learning": Search,
  "creative-design": Palette,
  "coding-development": Code,
  "project-management": Kanban
};

/**
 * AI Observatory Landing Page
 * Identifies, organizes, evaluates, and standardizes AI capabilities
 * Manages the Zodiac AI Capability Registry
 */
export default function AIObservatory() {
  const categoryStats = getCategoryStats();
  const labStats = getLabStats();
  const verifiedCount = getVerifiedTools().length;

  // Observatory functions
  const functions = [
    {
      icon: Search,
      title: "Identification",
      description: "Continuous scanning and identification of AI tools relevant to workforce intelligence development."
    },
    {
      icon: Filter,
      title: "Organization",
      description: "Systematic categorization of AI capabilities by function, industry, and application context."
    },
    {
      icon: CheckCircle,
      title: "Evaluation",
      description: "Rigorous assessment of AI tools for effectiveness, reliability, and integration readiness."
    },
    {
      icon: Tag,
      title: "Standardization",
      description: "Establishing standards for AI tool usage across Zodiac Lab programs and pathways."
    },
    {
      icon: Database,
      title: "Registry Management",
      description: "Maintaining the Zodiac AI Capability Registry as the central source of truth."
    },
    {
      icon: Layers,
      title: "Lab Integration",
      description: "Curating Lab AI Tool Libraries for each Zodiac Lab application context."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="ai-observatory-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-2 mb-6">
              <Link to="/observatories" className="text-sm text-[#1E3A8A]/60 hover:text-[#1E3A8A]">
                Observatories
              </Link>
              <span className="text-[#1E3A8A]/40">/</span>
              <span className="text-sm text-[#1E3A8A] font-medium">AI Observatory</span>
            </div>

            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-6">
              <Cpu className="w-4 h-4" />
              AI Capability Intelligence
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
              AI Observatory
            </h1>
            <p className="text-xl font-semibold text-[#1E3A8A] mb-4">
              The Central AI Capability Intelligence System
            </p>
            <p className="text-base text-[#1E3A8A]/80 leading-relaxed mb-8 max-w-3xl">
              AI Observatory identifies, organizes, evaluates, and standardizes AI capabilities for workforce 
              integration. It manages the Zodiac AI Capability Registry and curates Lab AI Tool Libraries 
              for each Zodiac Lab context.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/observatories/ai/registry">
                <Button size="lg" className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                  Browse AI Registry
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/zodiac-lab">
                <Button size="lg" variant="outline" className="border-[#991B1B] text-[#991B1B] hover:bg-[#991B1B] hover:text-white">
                  Explore Labs
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registry Stats */}
      <section className="py-12 bg-[#1E3A8A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-mono text-4xl font-bold text-white">{AI_REGISTRY.length}</p>
              <p className="text-sm text-white/70 mt-1">AI Tools Catalogued</p>
            </div>
            <div>
              <p className="font-mono text-4xl font-bold text-white">{verifiedCount}</p>
              <p className="text-sm text-white/70 mt-1">Zodiac Verified</p>
            </div>
            <div>
              <p className="font-mono text-4xl font-bold text-white">{categoryStats.length}</p>
              <p className="text-sm text-white/70 mt-1">Categories</p>
            </div>
            <div>
              <p className="font-mono text-4xl font-bold text-white">3</p>
              <p className="text-sm text-white/70 mt-1">Lab Libraries</p>
            </div>
          </div>
        </div>
      </section>

      {/* Registry Overview */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
                Zodiac AI Capability Registry
              </h2>
              <p className="text-lg text-[#1E3A8A] mb-6">
                The central system for all AI tool identification and standardization across the Zodiac ecosystem.
              </p>
              <p className="text-[#1E3A8A]/80 mb-8">
                Every AI tool in the registry has been evaluated for workforce relevance, reliability, and 
                integration potential. Lab AI Tool Libraries draw from this registry to provide context-specific 
                recommendations for Workforce Lab, Organization Lab, and Institutional Lab.
              </p>
              <Link to="/observatories/ai/registry">
                <Button className="bg-[#1E3A8A] text-white hover:bg-[#1E40AF]">
                  Explore the Registry
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-4">
                {categoryStats.slice(0, 6).map((cat, index) => {
                  const Icon = CATEGORY_ICONS[cat.id] || Cpu;
                  return (
                    <Link key={cat.id} to={`/observatories/ai/registry?category=${cat.id}`}>
                      <Card className="border-[#E5E7EB] hover:shadow-md hover:border-[#1E3A8A]/30 transition-all cursor-pointer">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center">
                              <Icon className="w-5 h-5 text-[#1E3A8A]" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#991B1B]">{cat.name}</p>
                              <p className="text-xs text-[#1E3A8A]/60">{cat.count} tools</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Lab AI Tool Libraries */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Lab AI Tool Libraries
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Context-specific AI tool recommendations for each Zodiac Lab.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Workforce Lab Library */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-[#1E3A8A]/20 hover:border-[#1E3A8A]/40 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#991B1B' }}>
                    Workforce Lab
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/70 mb-4">
                    AI tools for individual workforce intelligence development.
                  </p>
                  <p className="text-2xl font-mono font-bold text-[#1E3A8A] mb-4">
                    {labStats.workforce} <span className="text-sm font-normal">tools</span>
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-xs text-[#1E3A8A]/60">Common use cases:</p>
                    <ul className="text-sm text-[#1E3A8A]/70 space-y-1">
                      <li>• Resume and cover letter writing</li>
                      <li>• Interview preparation</li>
                      <li>• Personal productivity</li>
                      <li>• Skills development</li>
                    </ul>
                  </div>
                  <Link to="/observatories/ai/registry?lab=workforce">
                    <Button variant="outline" className="w-full border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white">
                      View Library
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            {/* Organization Lab Library */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-[#2563EB]/20 hover:border-[#2563EB]/40 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
                    <Building2 className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#991B1B' }}>
                    Organization Lab
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/70 mb-4">
                    AI tools for organizational workforce intelligence.
                  </p>
                  <p className="text-2xl font-mono font-bold text-[#2563EB] mb-4">
                    {labStats.organization} <span className="text-sm font-normal">tools</span>
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-xs text-[#1E3A8A]/60">Common use cases:</p>
                    <ul className="text-sm text-[#1E3A8A]/70 space-y-1">
                      <li>• Workforce analytics</li>
                      <li>• Team communication</li>
                      <li>• Process automation</li>
                      <li>• Training & documentation</li>
                    </ul>
                  </div>
                  <Link to="/observatories/ai/registry?lab=organization">
                    <Button variant="outline" className="w-full border-[#2563EB] text-[#2563EB] hover:bg-[#2563EB] hover:text-white">
                      View Library
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            {/* Institutional Lab Library */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-[#3B82F6]/20 hover:border-[#3B82F6]/40 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center mb-4">
                    <Landmark className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-2" style={{ color: '#991B1B' }}>
                    Institutional Lab
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/70 mb-4">
                    AI tools for sector-level workforce intelligence.
                  </p>
                  <p className="text-2xl font-mono font-bold text-[#3B82F6] mb-4">
                    {labStats.institutional} <span className="text-sm font-normal">tools</span>
                  </p>
                  <div className="space-y-2 mb-6">
                    <p className="text-xs text-[#1E3A8A]/60">Common use cases:</p>
                    <ul className="text-sm text-[#1E3A8A]/70 space-y-1">
                      <li>• Research synthesis</li>
                      <li>• Policy analysis</li>
                      <li>• Sector benchmarking</li>
                      <li>• Evidence mapping</li>
                    </ul>
                  </div>
                  <Link to="/observatories/ai/registry?lab=institutional">
                    <Button variant="outline" className="w-full border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6] hover:text-white">
                      View Library
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Observatory Functions */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Observatory Functions
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              How the AI Observatory creates value for the Zodiac ecosystem.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {functions.map((func, index) => (
              <motion.div
                key={func.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center mb-4">
                      <func.icon className="w-6 h-6 text-[#1E3A8A]" />
                    </div>
                    <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                      {func.title}
                    </h3>
                    <p className="text-sm text-[#1E3A8A]/80">{func.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1E3A8A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Find the Right AI Tools
          </h2>
          <p className="text-white/80 mb-8">
            Browse the registry to find AI tools aligned with your workforce intelligence goals.
          </p>
          <Link to="/observatories/ai/registry">
            <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-14 px-8">
              Browse AI Registry
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
